import { useState } from 'react';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../util/toasts';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { Location } from '../../../clients/quipli-service/location/location';
import { dateTimeFromRequest, DispatchSearchRequest, locationsFromRequest } from '../DispatchV2';
import { DateTime } from 'luxon';
import {
  AssignDriverRequest,
  Dispatch,
  Order,
  OrderSearchRequest,
  SearchDispatchRequest,
  UpdateScheduledTimeRequest,
} from '../../../clients/quipli-service/order/order';
import {
  OrderCardProcessedDetails,
  OrderDirection,
  orderToOrderCardPropsMapper,
} from '../../home/dashboard-v2/dashboard-utils';

export interface DispatchCards extends Dispatch {
  mappedOrderData?: OrderCardProcessedDetails;
}

export interface DispatchCategory {
  label: string;
  isExpanded: boolean;
  dispatchCards: DispatchCards[];
}

const defaultCategories = [
  {
    label: 'Unassigned Deliveries',
    isExpanded: true,
    dispatchCards: [],
  },
  {
    label: 'Unassigned Pickups',
    isExpanded: true,
    dispatchCards: [],
  },
  {
    label: 'Overdue',
    isExpanded: true,
    dispatchCards: [],
  },
];

export const useDispatchV2Data = () => {
  const { listLocations, searchDispatch, searchOrders, getConfiguration, assignDriver, updateScheduledTime } = useAPI();
  const { getAdminsList } = useAdminApiV1();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<DispatchCategory[]>(defaultCategories);

  const [allDrivers, setAllDrivers] = useState<Admin[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]);

  const [dispatch, setDispatch] = useState<Dispatch[]>();

  const [unitTrackingViaUnitNum, setUnitTrackingViaUnitNum] = useState<boolean>(true);

  // This will be on the drag event for the cards :)
  const assignCard = async (
    dispatch: Dispatch,
    driverId: number | undefined,
    selectedDateTime: DateTime,
    request: DispatchSearchRequest,
  ) => {
    try {
      setLoading(true);
      const orderId = dispatch.orderId;

      const deliveryProducts = dispatch.products?.delivery?.map((d) => d.id) ?? [];
      const pickupProducts = dispatch.products?.pickup?.map((d) => d.id) ?? [];

      const assignDriverBody: AssignDriverRequest = {
        orderId,
        driverId,
        orderProductIds: {
          pickup: pickupProducts,
          delivery: deliveryProducts,
        },
      };

      await assignDriver(orderId, assignDriverBody);

      const updateScheduledTimeBody: UpdateScheduledTimeRequest = {
        orderId: orderId,

        orderProductIds: {
          pickup: pickupProducts,
          delivery: deliveryProducts,
        },
        scheduledTime: selectedDateTime,
      };

      await updateScheduledTime(orderId, updateScheduledTimeBody);

      await doSearchDispatch(request);
    } catch {
      toast('Failed to assign driver to order', 'error');
    } finally {
      setLoading(false);
    }
  };

  const doSearchDispatch = async (request: DispatchSearchRequest) => {
    try {
      setLoading(true);

      const config = await getConfiguration('CONF_UNIT_TRACKING_VIA');
      setUnitTrackingViaUnitNum(config?.value === '0');

      const admins = await getAdminsList();

      setAllDrivers(admins);

      const locations = (await listLocations()).filter((l) => l.isActive);

      setAllLocations(locations);

      setCategories(defaultCategories);

      const selectedLocationIds = locationsFromRequest(request, locations).map((l) => l.id);

      if (request.date == null || request.type == null || selectedLocationIds.length === 0) {
        setLoading(false);
        setDispatch([]);
        setCategoryDispatches('Unassigned Deliveries', [], [], OrderDirection.GoingOut, locations);

        setCategoryDispatches('Unassigned Pickups', [], [], OrderDirection.ComingIn, locations);

        setCategoryDispatches('Overdue', [], [], OrderDirection.ComingIn, locations);
        return;
      }

      const type = request.type && request.type !== 'Both' ? (request.type as 'Delivery' | 'Pickup') : undefined;
      const driverIds =
        request.drivers != undefined &&
        request.drivers.length > 0 &&
        !(request.drivers.length === 1 && request.drivers[0] == '') // to handle drivers = ['']
          ? request.drivers.map((d) => parseInt(d))
          : undefined;

      const date = dateTimeFromRequest(request);

      if (date == null) {
        setLoading(false);
        return;
      }

      const listViewSearchRequest: SearchDispatchRequest = {
        date,
        locationIds: selectedLocationIds,
        type,
        driverIds,
        hideQuotes: request.hideQuotes,
        pageSize: 100,
      };

      const result = await searchDispatch(listViewSearchRequest);

      setDispatch(result);

      const deliveriesRequestBase = {
        date,
        locationIds: selectedLocationIds,
        pageSize: 100,
        hideQuotes: request.hideQuotes,
        driverIds: undefined,
      };

      const allRequest: SearchDispatchRequest = {
        ...deliveriesRequestBase,
      };

      const allDispatch = await searchDispatch(allRequest);

      const overdueFiltered = allDispatch.filter(
        (r) =>
          (r.driver == undefined || r.scheduledTime == undefined) &&
          r.startTime < date.startOf('day') &&
          r.products.pickup &&
          r.products.pickup.length > 0,
      );

      const unassignedDeliveries = allDispatch.filter(
        (r) =>
          r.products.delivery &&
          r.products.delivery.length > 0 &&
          (r.driver == undefined || r.scheduledTime == undefined),
      );

      const unassignedPickups = allDispatch
        .filter(
          (r) =>
            r.products.pickup &&
            r.products.pickup.length > 0 &&
            (r.driver == undefined || r.scheduledTime == undefined),
        )
        .filter(
          // Filter out the ones that are in the overdue list
          (o) =>
            !overdueFiltered.find(
              (of) =>
                of.orderId === o.orderId &&
                of.driver === o.driver &&
                of.scheduledTime === o.scheduledTime &&
                of.products.pickup?.length === o.products.pickup?.length,
            ),
        );

      const unassignedDeliveryOrders: Order[] = [];
      const unassignedPickupOrders: Order[] = [];
      const overdueOrders: Order[] = [];

      for (const locationId of selectedLocationIds) {
        const baseOrderRequest: OrderSearchRequest = {
          pageNumber: 1,
          pageSize: 100,
          locationId,
        };

        /*
          TODO: right now, going to re-use the order cards which are pretty helpful,
          so for the time being will be getting the order information from the
          dispatch events, but possible to refactor to remove the need to do this,
          will just have to recreate the functionality using dispatch data only
        */
        if (selectedLocationIds) {
          for (const category of categories) {
            if (category.label === 'Unassigned Deliveries') {
              const orderRequest = { ...baseOrderRequest, ids: unassignedDeliveries.map((i) => i.orderId) };
              const orderResult = unassignedDeliveries.length > 0 ? await searchOrders(orderRequest) : { items: [] };

              unassignedDeliveryOrders.push(...orderResult.items);
            } else if (category.label === 'Unassigned Pickups') {
              const orderRequest = { ...baseOrderRequest, ids: unassignedPickups.map((i) => i.orderId) };
              const orderResult = unassignedPickups.length > 0 ? await searchOrders(orderRequest) : { items: [] };

              unassignedPickupOrders.push(...orderResult.items);
            } else if (category.label === 'Overdue') {
              const orderRequest = { ...baseOrderRequest, ids: overdueFiltered.map((i) => i.orderId) };
              const orderResult = overdueFiltered.length > 0 ? await searchOrders(orderRequest) : { items: [] };

              overdueOrders.push(...orderResult.items);
            }
          }
        }
      }
      setCategoryDispatches(
        'Unassigned Deliveries',
        unassignedDeliveries,
        unassignedDeliveryOrders,
        OrderDirection.GoingOut,
        locations,
      );

      setCategoryDispatches(
        'Unassigned Pickups',
        unassignedPickups,
        unassignedPickupOrders,
        OrderDirection.ComingIn,
        locations,
      );

      setCategoryDispatches('Overdue', overdueFiltered, overdueOrders, OrderDirection.ComingIn, locations);
    } catch (e) {
      toast('Unable to load dispatch list data', 'error');
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  // TODO: This should be consolidated, there's similar logic in the dispatch-card-modal, that could be reused
  const setCategoryDispatches = (
    label: string,
    dispatches: Dispatch[],
    orders: Order[],
    direction: OrderDirection,
    allLocations: Location[],
  ) => {
    setCategories((categories) =>
      categories.map((c) => {
        if (c.label === label) {
          return {
            ...c,
            dispatchCards: dispatches
              .filter(
                // Remove cycle billed essentially
                (d) =>
                  d &&
                  (direction !== OrderDirection.ComingIn ||
                    orders.find((o) => d.orderId === o.id)?.products.find((p) => p.rentalEndDate) != undefined),
              )
              .map((d) => {
                const dispatchProducts = (
                  direction === OrderDirection.ComingIn ? d.products.pickup : d.products.delivery
                )?.map((p) => p.id);

                const order = orders.find((o) => d.orderId === o.id)!;

                const justProductsInThisDispatchOrder: Order = {
                  ...orders.find((o) => d.orderId === o.id)!,
                  products: order.products.filter((op) => dispatchProducts?.includes(op.id)),
                };

                console.log('order', order, allLocations);

                return {
                  ...d,
                  mappedOrderData:
                    orders.length > 0
                      ? {
                          ...orderToOrderCardPropsMapper(
                            justProductsInThisDispatchOrder,
                            DateTime.local(),
                            allLocations.find((al) => al.id.toString() === order.locationId.toString()),
                            [direction],
                          ),
                          scheduledDate: d.scheduledTime,
                          pickupDriverId:
                            direction === OrderDirection.ComingIn
                              ? d.driver
                                ? d.driver.adminId
                                : undefined
                              : undefined,
                          deliveryDriverId:
                            direction === OrderDirection.GoingOut
                              ? d.driver
                                ? d.driver.adminId
                                : undefined
                              : undefined,
                          dispatchStatus: d.status,
                        }
                      : undefined,
                };
              }),
          };
        }
        return c;
      }),
    );
  };

  const toggleCategoryVisibility = (label: string) => {
    setCategories(
      categories.map((c) => {
        if (c.label === label) {
          return { ...c, isExpanded: !c.isExpanded };
        }
        return c;
      }),
    );
  };

  const driversDictionary = allDrivers.reduce<Record<number, Admin>>((acc, driver) => {
    acc[driver.adminId] = driver;
    return acc;
  }, {});

  return {
    categories,
    toggleCategoryVisibility,
    allDrivers,
    driversDictionary,
    loading,
    allLocations,
    dispatch,
    unitTrackingViaUnitNum,
    assignCard,
    reload: (request: DispatchSearchRequest) => {
      doSearchDispatch(request);
    },
  };
};
